// Ports of ios/Sources/Models/ActiveCall.swift (ActiveCall, CallSession, ActiveCallsParsing),
// ios/Sources/Calls/ActiveCallsStore.swift (the snapshot-vs-delta revision rule), the main-socket
// call events of ios/Sources/Networking/WebSocketManager.swift, and the ring decision in
// CallManager.onActiveCallsChanged — all pure, so the worker (which owns the store and the main
// socket) and the page (which owns the ring) share one tested rule set. shared/API.md §30.

export interface CallSession {
  sessionId: string;
  userId: string;
}

export interface ActiveCall {
  id: string;
  channelId: string;
  startAt: number;
  postId: string;
  ownerId: string;
  hostId: string;
  threadId: string;
  sessions: CallSession[];
}

function obj(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export const ActiveCalls = {
  /** Distinct non-empty user ids — the same person can connect more than once. */
  participantCount(call: ActiveCall): number {
    return new Set(call.sessions.map((s) => s.userId).filter(Boolean)).size;
  },

  /** Distinct user ids in join order (the row marker's faces). */
  uniqueUserIds(call: ActiveCall): string[] {
    const seen = new Set<string>();
    return call.sessions.map((s) => s.userId).filter((id) => id && !seen.has(id) && (seen.add(id), true));
  },

  fromJson(json: unknown, channelId: string): ActiveCall {
    const j = obj(json);
    const sessions: CallSession[] = [];
    for (const raw of Array.isArray(j.sessions) ? j.sessions : []) {
      const s = obj(raw);
      const sessionId = str(s.session_id);
      const userId = str(s.user_id);
      if (sessionId && userId) sessions.push({ sessionId, userId });
    }
    return {
      id: str(j.id),
      channelId,
      startAt: num(j.start_at),
      postId: str(j.post_id),
      ownerId: str(j.owner_id),
      hostId: str(j.host_id),
      threadId: str(j.thread_id),
      sessions,
    };
  },

  /** One row per channel of `GET /plugins/com.mattermost.calls/channels`; rows without a call drop. */
  fromJsonArray(value: unknown): ActiveCall[] | null {
    if (!Array.isArray(value)) return null;
    const out: ActiveCall[] = [];
    for (const raw of value) {
      const row = obj(raw);
      const channelId = str(row.channel_id);
      if (!channelId || !row.call || typeof row.call !== 'object') continue;
      const call = ActiveCalls.fromJson(row.call, channelId);
      if (call.id) out.push(call);
    }
    return out;
  },

  upsertingSession(call: ActiveCall, session: CallSession): ActiveCall {
    const index = call.sessions.findIndex((s) => s.sessionId === session.sessionId);
    const sessions = [...call.sessions];
    if (index >= 0) sessions[index] = session;
    else sessions.push(session);
    return { ...call, sessions };
  },

  removingSession(call: ActiveCall, sessionId: string): ActiveCall {
    return { ...call, sessions: call.sessions.filter((s) => s.sessionId !== sessionId) };
  },
};

/** What one main-socket event does to the table (WebSocketManager.swift's five cases). */
export type ActiveCallOp =
  | { kind: 'changed'; call: ActiveCall }
  | { kind: 'ended'; channelId: string }
  | { kind: 'joined'; channelId: string; session: CallSession }
  | { kind: 'left'; channelId: string; sessionId: string };

export function activeCallOp(envelope: { event?: string; data?: Record<string, unknown>; broadcast?: { channel_id?: string } }): ActiveCallOp | null {
  const data = obj(envelope.data);
  const fromBroadcast = str(envelope.broadcast?.channel_id);
  const fromData = str(data.channel_id);
  switch (envelope.event) {
    case 'custom_com.mattermost.calls_call_state': {
      const channelId = fromData || fromBroadcast;
      let call: unknown = data.call;
      if (typeof call === 'string') {
        try {
          call = JSON.parse(call);
        } catch {
          return null;
        }
      }
      if (!channelId || !call || typeof call !== 'object') return null;
      const parsed = ActiveCalls.fromJson(call, channelId);
      return parsed.id ? { kind: 'changed', call: parsed } : null;
    }
    case 'custom_com.mattermost.calls_call_start': {
      const channelId = fromBroadcast || fromData;
      const id = str(data.id);
      if (!channelId || !id) return null;
      return {
        kind: 'changed',
        call: { id, channelId, startAt: num(data.start_at), postId: str(data.post_id), ownerId: str(data.owner_id), hostId: str(data.host_id), threadId: str(data.thread_id), sessions: [] },
      };
    }
    case 'custom_com.mattermost.calls_call_end': {
      const channelId = fromBroadcast || fromData;
      return channelId ? { kind: 'ended', channelId } : null;
    }
    case 'custom_com.mattermost.calls_user_joined': {
      const channelId = fromBroadcast || fromData;
      const sessionId = str(data.session_id);
      const userId = str(data.user_id) || str(data.userID);
      return channelId && sessionId && userId ? { kind: 'joined', channelId, session: { sessionId, userId } } : null;
    }
    case 'custom_com.mattermost.calls_user_left': {
      const channelId = fromBroadcast || fromData;
      const sessionId = str(data.session_id);
      return channelId && sessionId ? { kind: 'left', channelId, sessionId } : null;
    }
    default:
      return null;
  }
}

/**
 * The live "which channels have a call right now" table (ActiveCallsStore.swift). A socket delta
 * that landed while a REST snapshot was in flight is newer than the HTTP answer: its channel keeps
 * the socket's result, including an end, so a slow snapshot cannot resurrect a finished call.
 */
export class ActiveCallsTable {
  private calls = new Map<string, ActiveCall>();
  private revision = 0;
  private channelRevisions = new Map<string, number>();

  constructor(private readonly onChanged: (calls: ActiveCall[]) => void = () => undefined) {}

  all(): ActiveCall[] {
    return [...this.calls.values()];
  }

  get(channelId: string): ActiveCall | undefined {
    return this.calls.get(channelId);
  }

  clearAll(): void {
    this.revision = 0;
    this.channelRevisions.clear();
    this.publish(new Map());
  }

  apply(op: ActiveCallOp): void {
    switch (op.kind) {
      case 'changed': {
        this.noteDelta(op.call.channelId);
        const next = new Map(this.calls);
        next.set(op.call.channelId, op.call);
        this.publish(next);
        return;
      }
      case 'ended': {
        this.noteDelta(op.channelId);
        const next = new Map(this.calls);
        next.delete(op.channelId);
        this.publish(next);
        return;
      }
      case 'joined': {
        const call = this.calls.get(op.channelId);
        if (!call) return;
        this.noteDelta(op.channelId);
        const next = new Map(this.calls);
        next.set(op.channelId, ActiveCalls.upsertingSession(call, op.session));
        this.publish(next);
        return;
      }
      case 'left': {
        const call = this.calls.get(op.channelId);
        if (!call) return;
        this.noteDelta(op.channelId);
        const remaining = ActiveCalls.removingSession(call, op.sessionId);
        const next = new Map(this.calls);
        // The plugin broadcasts `call_end` ONLY on a host force-end; a call that simply EMPTIES
        // sends the last `user_left` and nothing more — clients end it themselves (the 1.0.138 bug).
        if (remaining.sessions.length === 0) next.delete(op.channelId);
        else next.set(op.channelId, remaining);
        this.publish(next);
        return;
      }
    }
  }

  /** The revision to capture BEFORE a snapshot request goes out. */
  currentRevision(): number {
    return this.revision;
  }

  applySnapshot(calls: ActiveCall[], startedAtRevision: number): void {
    const next = new Map(calls.map((c) => [c.channelId, c]));
    for (const [channelId, channelRevision] of this.channelRevisions) {
      if (channelRevision <= startedAtRevision) continue;
      const current = this.calls.get(channelId);
      if (current) next.set(channelId, current);
      else next.delete(channelId);
    }
    this.publish(next);
  }

  /** A server without Calls routes answered 404: capability absence, not stale state. */
  applyCapabilityAbsence(): void {
    this.publish(new Map());
  }

  private noteDelta(channelId: string): void {
    this.revision += 1;
    this.channelRevisions.set(channelId, this.revision);
  }

  private publish(next: Map<string, ActiveCall>): void {
    this.calls = next;
    this.onChanged(this.all());
  }
}

/**
 * CallManager.onActiveCallsChanged, as a pure decision: a call that is NEW (its id was not in the
 * previous table), started by SOMEONE ELSE, in a direct or group conversation, while this device is
 * idle and not already ringing → ring. A channel call is an event, not a summons: the banner covers it.
 */
export function incomingCallToRing(args: {
  calls: ActiveCall[];
  previousCallIds: ReadonlySet<string>;
  currentUserId: string | null;
  idle: boolean;
  alreadyRinging: boolean;
  channelType(channelId: string): string | null;
}): ActiveCall | null {
  if (!args.idle || args.alreadyRinging) return null;
  for (const call of args.calls) {
    if (!call.id || args.previousCallIds.has(call.id)) continue;
    if (!call.ownerId || call.ownerId === args.currentUserId) continue;
    const type = args.channelType(call.channelId);
    if (type === 'D' || type === 'G') return call;
    // Apple takes the FIRST fresh call by someone else and stops there, ringing or not.
    return null;
  }
  return null;
}

/** How long a ring lasts once somebody OTHER than the caller is on the call (a group someone answered, an invite). */
export const RING_ANSWERED_ELSEWHERE_MS = 45_000;
/** The ceiling for a ring nobody answers — a caller still waiting alone after this has given up in all but name. */
export const RING_MAX_MS = 10 * 60_000;
/** How often a ring re-asks the server whether the call is still waiting (the socket can be dead). */
export const RING_CHECK_MS = 15_000;

/**
 * Whether an incoming ring keeps going (user, 2026-10-22: "if there's no one actually connected do
 * not interrupt the ringing" — a fixed 45 s cut-off silenced calls whose caller was still waiting).
 * While only the CALLER is on the call, it rings until the call ends, up to RING_MAX_MS. It stops at
 * once when this account is on the call (answered on another device). Once anybody else is on it, or
 * when this device does not know the call at all, the old 45 s window applies.
 */
export function ringKeepsGoing(args: {
  call: ActiveCall | null;
  currentUserId: string | null;
  callerId: string;
  ringingMs: number;
}): boolean {
  if (args.ringingMs >= RING_MAX_MS) return false;
  if (!args.call) return args.ringingMs < RING_ANSWERED_ELSEWHERE_MS;
  const users = args.call.sessions.map((s) => s.userId).filter((id) => id !== '');
  if (args.currentUserId && users.includes(args.currentUserId)) return false;
  const caller = args.callerId || args.call.ownerId;
  if (users.some((id) => id !== caller)) return args.ringingMs < RING_ANSWERED_ELSEWHERE_MS;
  return true;
}
